import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Upload, Loader2, AlertCircle, CheckCircle, Copy, Check, Play, FolderPlus, Ban } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FileUploadItem } from '../contexts/UploadContext';
import { useAuth } from '../contexts/AuthContext';
import { trackEvent } from '../services/analytics';
import AddToCollectionModal from './AddToCollectionModal';

interface UnifiedUploadCardProps {
  files: FileUploadItem[];
  onRetry: (id: string) => void;
  // Optional: remove a queue item entirely. Used to render a "Dismiss" button
  // in place of "Retry" for unrecoverable failures (e.g. file-size limit) where
  // retrying the same file can never succeed.
  onDismiss?: (id: string) => void;
  // Hide the header "Add tracks to collection" CTA when callers have already
  // auto-attached the files (e.g. dropzone on the collection detail page).
  hideAddToCollectionCTA?: boolean;
}

// Failures with these messages can never succeed by retrying the same file —
// show "Dismiss" instead. Keep this list narrow; the default for a failed
// upload is still "Retry" (network/timeouts/server hiccups).
const isUnrecoverableFailure = (item: FileUploadItem): boolean => {
  if (!item.error) return false;
  return /file too large/i.test(item.error);
};

// Narrower predicate: the file failed specifically because it exceeded the
// plan's size limit. We branch the CTA on this — size-failure has an upgrade
// path (free → Pro), other unrecoverable failures don't.
const isSizeLimitFailure = (item: FileUploadItem): boolean => {
  if (!item.error) return false;
  return /file too large/i.test(item.error);
};

const FREE_LIMIT_MB = 50;

const formatFileSizeMB = (bytes: number): string => {
  const mb = bytes / (1024 * 1024);
  if (mb >= 100) return `${Math.round(mb)}MB`;
  return `${mb.toFixed(mb < 10 ? 1 : 0)}MB`;
};

// SizeFailureRow — replaces the standard file-info row entirely for
// file-too-large failures (free / anonymous only; Pro/Beta hits the 1500MB
// ceiling and falls through to the bare-error path because no upgrade exists).
//
// Design intent (v2): the failed row IS the message. No nested CTA card, no
// bolted-on sales panel — the row's own surface carries everything. The
// file's actual size is the visual anchor (text-4xl, soundwave-orange,
// tabular-nums), the free-tier limit is the quieter comparator beside it, and
// a single horizontal capacity meter underneath shows the overshoot
// graphically — same gradient as the uploading progress bar, but with a
// "cliff" marker at the free-tier limit and the file's size extending past
// it. The actions are text-buttons (lower weight than v1's filled chip) so
// the visual hierarchy is: number first, meter second, sentence third,
// actions fourth. Dismiss is always reachable.
//
// Both auth states land the user on /pro to READ — anon and signed-in free
// alike. We deliberately do NOT auto-forward to Stripe checkout from this
// funnel: a size-blocked free user is a curious person who just bumped into
// a limit, not a high-intent buyer. The /pro page itself decides what to
// show next based on auth state (its own sign-in / upgrade CTAs).
//
// Tone follows the project's use-case-led copy posture: invite, don't push.
// State what they hit, point at where the answer lives, stop. No feature
// list, no urgency language, no sign-in gate just to read about a plan.
interface SizeFailureRowProps {
  item: FileUploadItem;
  onDismiss?: (id: string) => void;
}

const SizeFailureRow: React.FC<SizeFailureRowProps> = ({ item, onDismiss }) => {
  const navigate = useNavigate();
  const { getValidToken } = useAuth();
  const prefersReducedMotion = useReducedMotion();
  const fileSizeMB = item.file.size / (1024 * 1024);
  const fileSizeLabel = formatFileSizeMB(item.file.size);
  // Capacity meter: 50MB free limit anchors the left half; the file's marker
  // extends past it. Clamp the meter's max at 4× the free limit (200MB) so a
  // 1.5GB monster doesn't make the cliff line invisibly close to the left
  // edge — the point of the visual is "you went past," not "by how many
  // multiples." Anything beyond 4× pegs the marker at the right edge.
  const meterMaxMB = Math.max(FREE_LIMIT_MB * 4, fileSizeMB);
  const cliffPct = (FREE_LIMIT_MB / meterMaxMB) * 100;
  const filePct = Math.min((fileSizeMB / meterMaxMB) * 100, 100);

  const handleUpgradeClick = () => {
    // Telemetry — extends the existing upgrade_button_clicked stream with the
    // upload_size_failure Source dimension. trackEvent is a no-op for anon
    // (no token); anon click-rate falls out via CloudFront delta. The event
    // name is now slightly misleading (the click goes to a read page, not a
    // checkout) but renaming would break the existing funnel widget — kept
    // as-is on purpose so the source split keeps reporting.
    trackEvent('upgrade_button_clicked', getValidToken, { source: 'upload_size_failure' });

    // Both anon and signed-in: land on /pro and let them read. No
    // ?intent=upgrade — that param drives Pro.tsx's auto-resume to Stripe,
    // which is the right behavior for users who click the Pro page's own
    // sign-in CTA but the wrong pressure for someone who just bumped into
    // the free-tier size cap.
    navigate('/pro');
  };

  const primaryLabel = 'See what Pro is';

  return (
    <div>
      {/* Status label — the row's first impression. Glenn's correction (v3):
          without an explicit failure morpheme, the orange hero number below
          reads as progress / spec / price, not as a rejection. This label
          fixes that at the visual entry point.

          Treatment is the project's quiet-label idiom (text-xs font-semibold
          uppercase tracking-wide — same as the "Latest audio" and section
          headers on /@handle and elsewhere), so it reads as the labeling
          system already in use (Law of Similarity), not new chrome. Tinted
          soundwave to tie to the hero number (Gestalt: same hue = same role)
          without going alarm-red — this is a free-tier ceiling, not data
          loss, and the project's tone is humble. The Ban glyph adds a
          non-color signal so we don't lean on hue alone (a11y).

          aria-live so AT users hear "Upload stopped — file too large" when
          the row swaps from active to failed. */}
      <div
        className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-soundwave-400/90"
        role="status"
        aria-live="polite"
      >
        <Ban className="w-3.5 h-3.5" strokeWidth={2.25} aria-hidden="true" />
        <span>Upload stopped — file too large</span>
      </div>

      {/* Filename (de-emphasized — confirms WHICH file when several are in
          the queue). Truncate at one line. */}
      <p className="text-xs truncate text-white/50 mt-2">
        {item.file.name}
      </p>

      {/* Hero row: prefix "Your file:" makes the relationship explicit —
          this is the file's size compared to the free-tier limit, not a
          progress reading. Same-baseline pairing of the file's size (visual
          anchor) and the limit (quieter comparator). */}
      <div className="flex items-baseline gap-2 mt-1 flex-wrap">
        <span className="text-xs text-white/50">Your file:</span>
        <span className="text-4xl font-bold tabular-nums leading-none text-soundwave-500">
          {fileSizeLabel}
        </span>
        <span className="text-sm text-white/50">
          / {FREE_LIMIT_MB}MB free
        </span>
      </div>

      {/* Capacity meter — the data IS the message. The free tier occupies
          cliffPct% of the bar; the file's size extends to filePct%. The
          cliff is a 1px vertical white line at the free-tier boundary; the
          fill uses the same soundwave→neon gradient as the active-upload
          progress bar, so this reads as a sibling of the upload state rather
          than as a new visual idiom.

          Reduced-motion users get the same final state without the
          width-from-0 animation. */}
      <div className="mt-3 relative h-1.5 bg-white/10 rounded-full overflow-hidden">
        <motion.div
          className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-soundwave-500 to-neon-500"
          initial={prefersReducedMotion ? false : { width: 0 }}
          animate={{ width: `${filePct}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-y-0 w-px bg-white/60"
          style={{ left: `${cliffPct}%` }}
          aria-hidden="true"
        />
      </div>

      {/* Single-sentence context line + action row on one strip. Actions are
          text-buttons (no chip background) so the visual weight goes to the
          size figure above, not down here. soundwave-400 + underline-on-hover
          is the project's inline-link idiom. */}
      <div className="mt-2.5 flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-white/60 leading-relaxed">
          The Pro plan allows single files up to 1.5GB.
        </p>
        <div className="flex items-center gap-4">
          {onDismiss && (
            <button
              onClick={() => onDismiss(item.id)}
              // inline-flex + min-h-[44px] meets Apple HIG mobile tap target
              // (44×44). The negative margin (-my-2.5) keeps the visible
              // layout collapsed so the button doesn't appear visually bigger
              // than the link-style treatment implies. Refactoring UI: tap
              // targets are a hit-area concern, not a visual-weight one.
              className="inline-flex items-center min-h-[44px] -my-2.5 text-xs text-white/40 hover:text-white/70 hover:underline underline-offset-2 decoration-white/40 transition-colors"
              type="button"
            >
              Dismiss
            </button>
          )}
          <button
            onClick={handleUpgradeClick}
            className="inline-flex items-center min-h-[44px] -my-2.5 text-sm font-medium text-soundwave-400 hover:text-soundwave-300 hover:underline underline-offset-2 decoration-soundwave-400 transition-colors"
            type="button"
          >
            {primaryLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

const UnifiedUploadCard: React.FC<UnifiedUploadCardProps> = ({
  files,
  onRetry,
  onDismiss,
  hideAddToCollectionCTA = false,
}) => {
  const navigate = useNavigate();
  const { isAuthenticated, isProUser, isBetaUser, getValidToken } = useAuth();
  const prefersReducedMotion = useReducedMotion();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showCollectionModal, setShowCollectionModal] = useState(false);

  const completedFileIds = files
    .filter(f => f.status === 'completed' && f.result?.uploadId)
    .map(f => f.result!.uploadId);

  // Fire a one-shot attention animation on the CTA every time a NEW file
  // finishes uploading. We bump a key on each upward transition; Framer
  // Motion remounts the animated element and replays the keyframes once.
  // No infinite pulse — that would be hostile after the user has noticed.
  const [attentionKey, setAttentionKey] = useState(0);
  const prevCompletedCountRef = useRef(0);
  useEffect(() => {
    const prev = prevCompletedCountRef.current;
    const curr = completedFileIds.length;
    if (curr > prev) {
      setAttentionKey(k => k + 1);
    }
    prevCompletedCountRef.current = curr;
  }, [completedFileIds.length]);

  // Impression tracking for the size-failure upgrade CTA. We need a
  // DENOMINATOR for the click event (upgrade_button_clicked +
  // source=upload_size_failure) so the AdminStatus funnel widget can render
  // a real CTR. Fired ONCE per item.id — re-renders, list re-sorts, and
  // sibling state churn must not double-count. We track fired ids in a Set
  // ref (not state, so the impression-firing effect doesn't trigger renders).
  //
  // Suppressed for Pro/Beta hitting the 1500MB ceiling because that branch
  // shows no CTA — counting impressions where no CTA was rendered would
  // pollute the funnel.
  const firedImpressionsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (isProUser() || isBetaUser()) return;
    for (const item of files) {
      if (
        item.status === 'failed' &&
        isSizeLimitFailure(item) &&
        !firedImpressionsRef.current.has(item.id)
      ) {
        firedImpressionsRef.current.add(item.id);
        trackEvent('upgrade_cta_shown', getValidToken, { source: 'upload_size_failure' });
      }
    }
  }, [files, isProUser, isBetaUser, getValidToken]);

  if (files.length === 0) {
    return null;
  }

  const copyToClipboard = async (url: string, id: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatFileSize = (bytes: number): string => {
    const mb = bytes / 1024 / 1024;
    return mb < 1 ? `${(bytes / 1024).toFixed(0)} KB` : `${mb.toFixed(1)} MB`;
  };

  // Video sources get honest labels — "Uploading video" while the container
  // is in flight to S3, "Extracting audio" while the audio-processor Lambda
  // pulls the audio stream via ffmpeg. Audio sources keep the generic labels.
  const getStatusInfo = (status: FileUploadItem['status'], file?: File) => {
    const isVideo = !!file && file.type.startsWith('video/');
    switch (status) {
      case 'queued':
        return { label: 'Queued', color: 'var(--text-tertiary)', icon: 'queued' };
      case 'uploading':
        return { label: isVideo ? 'Uploading video' : 'Uploading', color: '#ff4800', icon: 'uploading' };
      case 'processing':
        return { label: isVideo ? 'Extracting audio' : 'Processing', color: '#06b6d4', icon: 'processing' };
      case 'completed':
        return { label: 'Completed', color: '#10b981', icon: 'completed' };
      case 'failed':
        return { label: 'Failed', color: '#ef4444', icon: 'failed' };
      default:
        return { label: 'Unknown', color: 'var(--text-tertiary)', icon: 'queued' };
    }
  };

  // Honest subtotals. Each number means what it says — nothing derived from a
  // shifting whole. Two earlier attempts at a single "X of Y" reading both
  // lied in different ways: "Active" counted queued+uploading+processing+
  // failed and ticked DOWN as work progressed; "N of total" used files.length
  // as a denominator that shrinks when items are dismissed (size-failure
  // auto-dismiss, manual X) so the floor moves under the numerator. The fix
  // is to stop summarising and start naming.
  //
  // - inFlight: actual work the queue is doing or about to do. Goes UP when
  //   users drop more files in, DOWN as items complete. This is the only
  //   number that anchors "is it almost over."
  // - completedCount / failedCount: each is honest about its own state.
  // - allDone: nothing left to wait on AND something happened.
  const completedCount = files.filter(f => f.status === 'completed').length;
  const failedCount = files.filter(f => f.status === 'failed').length;
  const inFlightCount = files.filter(
    f => f.status === 'queued' || f.status === 'uploading' || f.status === 'processing'
  ).length;
  const allDone = inFlightCount === 0 && (completedCount > 0 || failedCount > 0);

  // Render in insertion order. We used to re-sort on every render (active →
  // queued → failed → completed) but during multi-upload that yanks rows up
  // and down every time a file flips state, and Framer's `layout`
  // interpolation collides with the row-content swap — text from the
  // outgoing and incoming row briefly overlap (visible as garbled filenames
  // mid-animation). Keeping the order stable means each row mutates in
  // place; no row jumps, no overlap.
  const sortedFiles = files;

  return (
    <>
    <motion.div
      className="rounded-2xl border p-6 shadow-sm"
      style={{ backgroundColor: 'var(--background-surface)', borderColor: 'var(--border-color)' }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      layout
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center shadow-lg">
          <Upload className="w-6 h-6 text-white" strokeWidth={2} />
        </div>
        <div className="min-w-0">
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            {allDone ? 'Upload Summary' : 'Upload Queue'}
          </p>
          {/* Primary reading: how much work is left, not a derived ratio.
              "X to go" is non-decreasing in intent (only goes down as work
              finishes; goes up if the user drops in more files, which they
              expect). When done, name the success state. Quiet subtotal strip
              below splits the honest counts so no single number has to carry
              an ambiguous meaning. */}
          <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            {allDone
              ? completedCount > 0 && failedCount === 0
                ? 'All done'
                : completedCount === 0 && failedCount > 0
                  ? 'Nothing uploaded'
                  : 'Done'
              : `${inFlightCount} to go`}
          </p>
          {/* Quiet subtotal strip — only renders when it adds information the
              big number doesn't already carry. Active state with only queued
              items shows nothing (the "X to go" IS that count). The strip
              appears as soon as something has completed or failed, so the
              user sees the breakdown of finished work alongside what remains. */}
          {(completedCount > 0 || failedCount > 0) && (
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
              {[
                completedCount > 0 ? `${completedCount} done` : null,
                failedCount > 0 ? `${failedCount} failed` : null,
              ].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>

        {isAuthenticated && !hideAddToCollectionCTA && completedFileIds.length > 0 && (
          <motion.button
            onClick={() => setShowCollectionModal(true)}
            className="ml-auto relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-soundwave-400 hover:text-soundwave-300 hover:bg-soundwave-500/10 transition-colors"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            // One-shot soft glow that retriggers on each new completion.
            key={`btn-${attentionKey}`}
            animate={prefersReducedMotion ? undefined : {
              boxShadow: [
                '0 0 0 0 rgba(255, 72, 0, 0)',
                '0 0 0 6px rgba(255, 72, 0, 0.18)',
                '0 0 0 0 rgba(255, 72, 0, 0)',
              ],
            }}
            transition={{ duration: 1.1, ease: 'easeOut' }}
          >
            <motion.span
              key={`icon-${attentionKey}`}
              className="inline-flex"
              animate={prefersReducedMotion ? undefined : {
                scale: [1, 1.25, 1, 1.15, 1],
                rotate: [0, -10, 8, -4, 0],
              }}
              transition={{ duration: 0.7, ease: 'easeOut', times: [0, 0.25, 0.5, 0.75, 1] }}
            >
              <FolderPlus className="w-4 h-4" />
            </motion.span>
            <span className="hidden sm:inline">
              Add {completedFileIds.length === 1 ? 'track' : `${completedFileIds.length} tracks`} to collection
            </span>
            <span className="sm:hidden">
              Add to collection
            </span>
          </motion.button>
        )}
      </div>

      {/* Unified File List */}
      <div className="space-y-3">
        <AnimatePresence mode="popLayout">
          {sortedFiles.map((item, index) => {
            const statusInfo = getStatusInfo(item.status, item.file);
            const isActive = item.status === 'uploading' || item.status === 'processing';
            const isFailed = item.status === 'failed';
            const isCompleted = item.status === 'completed';
            // The size-failure-with-upgrade-path branch transforms the row's
            // body wholesale (no nested CTA card — the row IS the message).
            // Pro/Beta hitting the 1500MB ceiling falls through to the
            // standard file-info header + bare-error + Dismiss path because
            // there's no upgrade to offer.
            const isUpgradableSizeFailure =
              isFailed && isSizeLimitFailure(item) && !isProUser() && !isBetaUser();

            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
                transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30,
                  delay: index * 0.03
                }}
                className="px-4 py-3 rounded-xl border relative overflow-hidden"
                style={{
                  borderColor: isCompleted
                    ? 'rgba(16, 185, 129, 0.3)'
                    : isUpgradableSizeFailure
                      ? 'rgba(255, 72, 0, 0.25)'
                      : 'var(--border-color)',
                  backgroundColor: isCompleted ? 'rgba(16, 185, 129, 0.05)' : 'rgba(0,0,0,0.2)'
                }}
                layout
              >
                <div className="relative z-10">
                  {isUpgradableSizeFailure ? (
                    /* In-place size-failure body — replaces the standard
                       file-info header entirely. No nested container; the
                       row's own surface carries the message. */
                    <SizeFailureRow item={item} onDismiss={onDismiss} />
                  ) : (
                  <>
                  {/* File Info Header */}
                  <div className="flex items-start gap-3">
                    {/* Status Icon */}
                    <div className="flex-shrink-0 pt-0.5">
                      {isActive && (
                        <Loader2 className="w-5 h-5 animate-spin" style={{ color: statusInfo.color }} />
                      )}
                      {isFailed && (
                        <AlertCircle className="w-5 h-5" style={{ color: statusInfo.color }} />
                      )}
                      {isCompleted && (
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                      )}
                      {item.status === 'queued' && (
                        <div className="w-5 h-5 rounded-full border-2" style={{ borderColor: statusInfo.color }} />
                      )}
                    </div>

                    {/* File Details */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                        {item.file.name}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                          {formatFileSize(item.file.size)}
                        </p>
                        <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>•</span>
                        <p className="text-xs font-medium" style={{ color: statusInfo.color }}>
                          {statusInfo.label}
                        </p>
                      </div>
                    </div>

                    {/* Actions - Right Side */}
                    <div className="flex items-center gap-2">
                      {/* Completed File Actions */}
                      {isCompleted && item.result && (
                        <>
                          {/* Copy Link Button */}
                          <motion.button
                            onClick={() => copyToClipboard(item.result!.shareUrl, item.id)}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg transition-colors"
                            style={{
                              backgroundColor: copiedId === item.id ? 'rgba(16, 185, 129, 0.2)' : 'var(--background-secondary)',
                              color: copiedId === item.id ? 'rgb(16, 185, 129)' : 'var(--text-secondary)',
                            }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            title={copiedId === item.id ? 'Copied!' : 'Copy link'}
                          >
                            {copiedId === item.id ? (
                              <Check className="w-4 h-4" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                            <span className="text-sm font-semibold hidden sm:inline">
                              {copiedId === item.id ? 'Copied!' : 'Copy Link'}
                            </span>
                          </motion.button>

                          {/* Play Link - Text-based with icon */}
                          <motion.button
                            onClick={() => navigate(`/player/${item.result!.uploadId}`)}
                            className="flex items-center gap-1 text-sm font-medium text-cyan-500 hover:text-cyan-400 transition-colors"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            title="Play file"
                          >
                            <Play className="w-3.5 h-3.5" />
                            <span>Play</span>
                          </motion.button>
                        </>
                      )}

                    </div>
                  </div>

                  {/* Progress Bar - Only for active uploads */}
                  {isActive && (
                    <div className="space-y-2 mt-3">
                      <div className="flex justify-between text-xs">
                        <span style={{ color: 'var(--text-tertiary)' }}>
                          {item.progress}%
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full rounded-full bg-gradient-to-r from-soundwave-500 to-neon-500"
                          initial={{ width: '0%' }}
                          animate={{ width: `${item.progress}%` }}
                          transition={{ duration: 0.3, ease: 'easeOut' }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Error message + action — two branches now (size-failure
                      with upgrade path is handled by SizeFailureRow above):
                      1. Size-limit failure + Pro/Beta → no upgrade path; bare error + Dismiss.
                      2. Any other failure (network/server/etc.) → bare error + Retry. */}
                  {isFailed && (
                    <div className="mt-3 flex items-center gap-2">
                      <p className="text-xs text-red-400 flex-1">
                        {item.error || 'Upload failed'}
                      </p>
                      {isUnrecoverableFailure(item) && onDismiss ? (
                        <motion.button
                          onClick={() => onDismiss(item.id)}
                          className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 text-xs font-medium transition-colors"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          Dismiss
                        </motion.button>
                      ) : (
                        <motion.button
                          onClick={() => onRetry(item.id)}
                          className="px-3 py-1.5 rounded-lg bg-soundwave-500/20 hover:bg-soundwave-500/30 text-soundwave-400 text-xs font-medium transition-colors"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          Retry
                        </motion.button>
                      )}
                    </div>
                  )}
                  </>
                  )}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </motion.div>

    <AddToCollectionModal
      isOpen={showCollectionModal}
      onClose={() => setShowCollectionModal(false)}
      fileIds={completedFileIds}
    />
    </>
  );
};

export default UnifiedUploadCard;
